<template>
	<time
		v-if="iso"
		:datetime="iso"
	>{{ label }}</time>
</template>

<script setup>
import dayjs from "dayjs";
import { useMounted } from "@vueuse/core";
import { daysAgo } from "~/utils/generic";

const props = defineProps({
	date: {
		type: [String, Number, Date],
		default: null
	},
	// Mirrors daysAgo()'s second arg (on-page granularity). Defaults to true
	// because every current call site passes true.
	isOnPage: {
		type: Boolean,
		default: true
	}
});

const mounted = useMounted();

// ISO string for the <time datetime> attribute — deterministic (no "now"),
// so it's identical on server and client and never causes drift.
const iso = computed(() => {
	const d = dayjs(props.date);
	return props.date && d.isValid() ? d.toISOString() : "";
});

// Deterministic absolute label used on the server AND the first client render,
// so the hydrated markup matches byte-for-byte.
const absolute = computed(() => {
	const d = dayjs(props.date);
	return d.isValid() ? d.format("MMM D, YYYY") : "";
});

// After mount we swap to the relative "X hours ago" label. This runs
// client-only, so it can't drift from the server's clock and won't force Vue
// to discard the hydrated tree.
const label = computed(() =>
	mounted.value ? daysAgo(props.date, props.isOnPage) : absolute.value
);
</script>
